import submitRedemption from "@aspen/Handlers/SubmitRedemptionForm";
import { SubmitRedemptionFormApiParameter } from "@aspen/Services/AspenEmailService";
import AspenSettings from "@aspen/Store/Aspen/Types/AspenSettings";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import submitContactForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SubmitContactForm";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import { RefObject} from "react";
import ReCAPTCHA from "react-google-recaptcha";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker from "@insite/mobius/DatePicker";
import FileUpload from "@insite/mobius/FileUpload";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Trash from "@insite/mobius/Icons/Trash";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import floor from "lodash/floor";
import React, { ChangeEvent, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
  emailRecipients = "emailRecipients",
  submitButtonText = "submitButtonText",
  successMessage = "successMessage",
  invoiceAttachmentAcceptedFileTypesNote = "invoiceAttachmentAcceptedFileTypesNote",
  invoiceAttachmentNote = "invoiceAttachmentNote",
  invoiceNumberNote = "invoiceNumberNote",
  modelNumbersNote = "modelNumbersNote",
  serialNumbersNote = "serialNumbersNote",
  title = "title",
  instructions = "instructions",
  incorrectFileTypeMessage = "incorrectFileTypeMessage",
  fileSizeLimitMessage = "fileSizeLimitMessage",
  maxFileCountLimitMessage = "maxFileCountLimitMessage"
}

interface OwnProps extends WidgetProps {
  fields: {
    [fields.incorrectFileTypeMessage]: string;
    [fields.maxFileCountLimitMessage]: string;
    [fields.fileSizeLimitMessage]: string;
    [fields.emailRecipients]: string;
    [fields.submitButtonText]: string;
    [fields.successMessage]: string;
    [fields.title]: string;
    [fields.instructions]: string;
    [fields.invoiceAttachmentAcceptedFileTypesNote]: string;
    [fields.invoiceAttachmentNote]: string;
    [fields.invoiceNumberNote]: string;
    [fields.modelNumbersNote]: string;
    [fields.serialNumbersNote]: string;
  };
}
const mapStateToProps = (state: ApplicationState) => {
  const { SpecialtyOptions, AreaOfInterestOptions, PromotionToRedeemOptions, Countries, USStates, CanadaStates } = (getSettingsCollection(state) as any).aspenSettings as AspenSettings;
  return {
    SpecialtyOptions: SpecialtyOptions!.split(','),
    AreaOfInterestOptions: AreaOfInterestOptions!.split(','),
    PromotionToRedeemOptions: PromotionToRedeemOptions!.split(','),
    Countries: Countries!.split(','),
    USStates: USStates!.split(','),
    CanadaStates: CanadaStates!.split(',')
  }
};

const mapDispatchToProps = {
  submitContactForm,
  submitRedemption,
  clearForm,
  validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
    validateReCaptcha,
    ),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RedemptionFormStyles {
  container?: InjectableCss;
  formGridItem?: GridItemProps;
  submitButton?: ButtonPresentationProps;
  successModal?: ModalPresentationProps;
  modalContainer?: GridContainerProps;
  messageTextGridItem?: GridItemProps;
  messageText?: TypographyPresentationProps;
  buttonGridItem?: GridItemProps;
  continueButton?: ButtonPresentationProps;
  fileUploadBox?: InjectableCss;
}

export const redemptionFormStyles: RedemptionFormStyles = {
  formGridItem: {
    width: 12, css: css`
    margin-top:10px;
  ` },
  submitButton: {
    css: css`
            float: left;
            margin-top: 20px;
        `,
  },
  successModal: { sizeVariant: "small" },
  messageTextGridItem: { width: 12 },
  buttonGridItem: {
    width: 12,
    css: css`
            justify-content: flex-end;
        `,
  },
  container:{
    css: css`
          @media (max-width: 767px) {
            padding:20px;
          }
          .wpb_wrapper{
            width: 100%;
          }
        `
  }
};

const styles = redemptionFormStyles;
const emailsRegexp = new RegExp(
  "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);

type Validator = () => boolean;
type ContextType = {
  validators: {
    [key: string]: Validator | undefined;
  };
};

export const RedemptionFormContext = React.createContext<ContextType>({
  validators: {},
});

const RedemptionForm = ({ id, fields, submitContactForm, submitRedemption, clearForm, validateReCaptcha, AreaOfInterestOptions, CanadaStates, Countries, PromotionToRedeemOptions, SpecialtyOptions, USStates }: Props) => {
  const { emailRecipients, submitButtonText, successMessage } = fields;
  const [validators] = useState<{ [key: string]: Validator | undefined }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();
  const modalCloseHandler = () => {
    setIsModalOpen(false);
    clearForm();
    window.location.reload();
  }

  const continueButtonClickHandler = () => {
    setIsModalOpen(false);
    clearForm();
    window.location.reload();
  };
  const [promotionToRedeem, setPromotionToRedeem] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [todayDate, setTodayDate] = useState<Date>(new Date());
  const [specialty, setSpecialty] = useState('');
  const [areaOfInterest, setAreaOfInterest] = useState('');
  const [practiceHospital, setPracticeHospital] = useState('');
  const [country, setCountry] = useState('United States');
  const [streetAddress, setStreetAddress] = useState('');
  const [streetAddress2, setStreetAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [serialNumbers, setSerialNumbers] = useState('');
  const [modelNumbers, setModelNumbers] = useState('');
  const [invoiceNumbers, setInvoiceNumbers] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [physicianLicense, setPhysicianLicense] = useState('');
  const [salesRepOrDistributor, setSalesRepOrDistributor] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [maxSizeReached, setMaxSizeReached] = useState(false);
  const [incorrectFileExtension, setIncorrectFileExtension] = useState(false);
  const [maxFileCountReached, setMaxFileCountReached] = useState(false);
  const [showValidations, setShowValidations] = useState(false);
  const [badFiles, setBadFiles] = useState<File[]>([]);
  const [isFileUploadDivRemoved, setIsFileUploadDivRemoved] = useState(false);
  const [uploadFileName, setUploadFileName] = useState('');
  const clearRedemptionForm = () => {
    setFirstName('');
    setLastName('');
    setPromotionToRedeem('');
    setSpecialty('');
    setAreaOfInterest('');
    setPracticeHospital('');
    setCountry('United States');
    setStreetAddress('');
    setStreetAddress2('');
    setCity('');
    setState('');
    setZip('');
    setPhone('');
    setEmail('');
    setSerialNumbers('');
    setModelNumbers('');
    setInvoiceNumbers('');
    setPhysicianLicense('');
    setSalesRepOrDistributor('');
    setAdditionalNotes('');
  }
  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowValidations(true);
    const widgetValidators: Validator[] = [];
    Object.keys(validators).forEach(key => {
      const validator = validators[key];
      if (validator) {
        widgetValidators.push(validator);
      }
    });
    const results = widgetValidators.map(v => v());
    if (!results.every(o => o)) {
      return;
    }

    event.preventDefault();

    let fileUpload = files?.length > 0 ? true : false;

    if (!todayDate || !firstName || !lastName || !specialty || !areaOfInterest || !practiceHospital || !city || !state || !zip || !phone || !email || !serialNumbers || !modelNumbers || !invoiceNumbers || !physicianLicense || !salesRepOrDistributor || !fileUpload) {
      // alert('Please fill out all required fields.');
      return;
    }
    const formData = new FormData();
    formData.append('firstName', firstName);
    formData.append('lastName', lastName);
    formData.append('specialty', specialty);
    formData.append('areaOfInterest', areaOfInterest);
    formData.append('practiceHospital', practiceHospital);
    formData.append('country', country);
    formData.append('streetAddress', streetAddress);
    formData.append('streetAddress2', streetAddress2);
    formData.append('city', city);
    formData.append('state', state);
    formData.append('zip', zip);
    formData.append('phone', phone);
    formData.append('email', email);
    formData.append('serialNumbers', serialNumbers);
    formData.append('modelNumbers', modelNumbers);
    formData.append('invoiceNumbers', invoiceNumbers);
    formData.append('physicianLicense', physicianLicense);
    formData.append('salesRepOrDistributor', salesRepOrDistributor);
    formData.append('additionalNotes', additionalNotes);
    formData.append('emailTo', fields.emailRecipients);
    formData.append('todayDate', todayDate.toDateString());
    formData.append('emailRecipients', fields.emailRecipients);
    formData.append('promotionToRedeem', promotionToRedeem);
    files.forEach((file, index) => {
      formData.append(`file${index}`, file); // Use a unique key for each file
    });
    
    const isReCaptchaValid = await validateReCaptcha({ location: "", recaptchaRef });
    if (!isReCaptchaValid) {
      return;
    }
    setIsSubmitting(true);
    
      formPost(formData);
    
  };
  const formPost = async (formData:any) => {
    try {
      const emailsUrl = "/api/v1/promotionredemption";
      const postResponse = await fetch(`${emailsUrl}/redeem`, {
        method: 'POST',
        body: formData,
      }).then(response => {
          setIsModalOpen(true);
          setIsSubmitting(false);
          setShowValidations(false);
          clearRedemptionForm();
      }).catch(error => {
        
      });     
          
    } catch (error) {
        //
    }
};
  const style = { width: '100%', background: '#ccc', textAlign: 'center' };
  const Content = ({ children }: any) => {
    return <Typography {...style}>{children}</Typography>;
  };
  const header = () => {
    return (<>
      <div className="wpb_wrapper">
        <h1>
          <span style={{ color: "#042359" }}>{fields.title}</span>
        </h1>
        {parse(fields.instructions)}
        <hr/>
      </div>
    </>);
  }
  
  const promotionToRedeemClicked = (e: any) => {
    setPromotionToRedeem(e.target.value);
  };

  const practiceHospitalChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPracticeHospital(value);

  };

  const firstNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFirstName(value);
  };
  const lastNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLastName(value);
  };
  const streetAddressChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setStreetAddress(value);
  };
  const streetAddress2ChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setStreetAddress2(value);
  };
  const todayDateChangeHandler = ({ selectedDay }: any) => {
    setTodayDate(selectedDay);
  };

  const specialtyOptions = [
    { key: "", value: "Choose One" }, ...SpecialtyOptions.map((value, index) => { return { key: value, value: decodeURIComponent(value) } })];
  const promotionToRedeemOptions = PromotionToRedeemOptions.map((value, index) => decodeURIComponent(value));
  const areaOfInterestOptions = [
    { key: "", value: "Choose One" }, ...AreaOfInterestOptions.map((value, index) => { return { key: value, value: decodeURIComponent(value) } })];
  const countries = [
    ...Countries.map((value, index) => { return { key: value, value: decodeURIComponent(value) } })];
  const usStates = [
    { key: "", value: "Choose One" }, ...USStates.map((value, index) => { return { key: value, value: decodeURIComponent(value) } })];
  const canadaStates = [
    { key: "", value: "Choose One" }, ...CanadaStates.map((value, index) => { return { key: value, value: decodeURIComponent(value) } })];

  const specialtyChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    if (value !== specialty) {
      setSpecialty(value);
    }
  }
  const areaOfInterestChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    if (value !== areaOfInterest) {
      setAreaOfInterest(value);
    }
  }
  const countryChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    if (value !== country) {
      setCountry(value);
      setState('');
    }
  }
  const stateChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    if (value !== state) {
      setState(value);
    }
  }
  const cityChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value !== state) {
      setCity(value);
    }
  }
  const zipChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value !== zip) {
      setZip(value);
    }
  }
  const phoneChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value !== phone) {
      setPhone(value);
    }
  };
  const emailChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value !== email) {
      setEmail(value);
    }
  };
  const serialNumberChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value !== serialNumbers) {
      setSerialNumbers(value);
    }
  };
  const modelNumberChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value !== modelNumbers) {
      setModelNumbers(value);
    }
  };
  const invoiceNumberChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value !== invoiceNumbers) {
      setInvoiceNumbers(value);
    }
  };
  const additionalNotesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    if (value !== additionalNotes) {
      setAdditionalNotes(value);
    }
  };
  const salesRepOrDistributorChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value !== salesRepOrDistributor) {
      setSalesRepOrDistributor(value);
    }
  };
  const physicianLicenseChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value !== physicianLicense) {
      setPhysicianLicense(value);
    }
  };
  const getFileExtension = (fileName: string) => {
    const splitFileName = fileName.split(".");
    return splitFileName.length > 0 ? splitFileName[splitFileName.length - 1].toLowerCase() : "";
  };
  function removeAppearanceTransitionDiv() {
    if (isFileUploadDivRemoved) {
        return;
    }
    let intervalFileUploadDiv = setInterval(() => {
      const fileUploadDiv = document.querySelector('div[class*="AppearanceTransition"]');
      if (fileUploadDiv) {
        fileUploadDiv?.parentNode?.removeChild(fileUploadDiv);
        setIsFileUploadDivRemoved(true); 
        clearInterval(intervalFileUploadDiv);
      }
  }, 100);
    
}

const fileChangeHandler = (event: React.ChangeEvent<any>) => {
  const uploadFiles = event.target.files as File[];
  const maxNumberOfFiles = 5;

  // Check if the total number of files exceeds the maximum limit
  const existingFilesCount = files ? files.length : 0;
  const totalFiles = existingFilesCount + uploadFiles.length;

  if (totalFiles >= maxNumberOfFiles) {
    setMaxFileCountReached(true);
  } else {
    setMaxFileCountReached(false);
  }

  // Clear bad files if no files are selected
  if (uploadFiles.length === 0) {
    setBadFiles([]);
  }

  let hasIncorrectFileExtension = false;
  let hasMaxSizeFile = false;
  
  Array.from(uploadFiles).forEach((file, index) => {
    if (index < maxNumberOfFiles - existingFilesCount) {
      if (file) {
        const fileExtension = getFileExtension(file.name);
        // Check if the file extension is incorrect
        if (["jpeg", "jpg", "pdf"].indexOf(fileExtension.toLowerCase()) === -1) {
          hasIncorrectFileExtension = true;
        }
        // Check if the file size exceeds the maximum limit
        if (file.size > 10485760) { 
          hasMaxSizeFile = true;
          setBadFiles(prevBadFiles => [...prevBadFiles, file]);
        }
      }
    }
  });

  
  setIncorrectFileExtension(hasIncorrectFileExtension);
  if (!hasIncorrectFileExtension && !hasMaxSizeFile) {
    const slicedFiles = Array.from(uploadFiles).slice(0, maxNumberOfFiles - existingFilesCount);
    const concatenatedNames = slicedFiles.map(file => file.name).join(',');
    setUploadFileName(concatenatedNames);
    setFiles(prevFiles => {
      const newFiles =slicedFiles;
      return prevFiles ? [...prevFiles, ...newFiles] : newFiles;
    });
  }

  
  if (!isFileUploadDivRemoved) {
    removeAppearanceTransitionDiv();
  }
};

  const Notes = ({ children }: any) => {
    return (<><br /><Typography style={{
      clear: "both",
      fontFamily: "inherit",
      fontSize: "15px",
      letterSpacing: "normal",
      lineHeight: "inherit",
      paddingTop: "13px",
      width: "100%",
    }}>{children}</Typography><br /></>)
  }
  const CountExceedNotes = ({ children }: any) => {
    return (<><br /><Typography style={{
      clear: "both",
      fontFamily: "inherit",
      fontSize: "15px",
      letterSpacing: "normal",
      lineHeight: "inherit",
      paddingTop: "13px",
      width: "100%",
      color:"#f96523"
    }}>{children}</Typography><br /></>)
  }

  function deleteFile(file: File): React.MouseEventHandler<HTMLSpanElement> | undefined {
    try {
      setFiles(prevFiles => {

        const updatedFiles = prevFiles.filter(f => f.name !== file.name);
        
        if (updatedFiles.length > 5) {
            setMaxFileCountReached(true);
        } else {
            setMaxFileCountReached(false);
        }

        const fileNames = updatedFiles.map(f => f.name);

        const concatenatedNames = fileNames.join(',');
        setUploadFileName(concatenatedNames);

        return updatedFiles;
      });
  } catch (error) {
      console.error("An error occurred while deleting the file:", error);
  }
    return undefined;
  }
  const error = "Field is required";
  const breakpoints = [8, 8, 8, 8, 8] as GridWidths[];
  const twoColumnsBreakpoints = [12, 12, 12, 12, 6] as GridWidths[];
  return (
    <>

      <GridContainer {...styles.container} gap={8}>
        <GridItem {...styles.formGridItem}>
          <RedemptionFormContext.Provider value={{ validators }}>
            <form id="redemptionForm" onSubmit={handleFormSubmit} noValidate>
              <GridContainer>
                <GridItem width={breakpoints} {...styles.formGridItem}>
                  {header()}
                </GridItem>              
                <GridItem width={breakpoints} {...styles.formGridItem}>
                  <RadioGroup horizontal={false} label="Select a Promotion to Redeem" name="promotionToRedeem" onChangeHandler={promotionToRedeemClicked} value={promotionToRedeem} style={{ display: "block!important" }} required error={showValidations && !promotionToRedeem ? error : ""}>
                    {promotionToRedeemOptions.map((option, index) => {
                      return <Radio key={index} style={{ display: "block!important", margin: "auto" }} value={option}>{option}</Radio>;
                    })}
                  </RadioGroup>
                </GridItem>
                <GridItem width={8} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}>
                      <div style={{ width: "100%" }}>
                        <TextField onChange={firstNameChangeHandler} label="Your Name"
                          value={firstName} required error={showValidations && !firstName ? error : ""}
                        /><label>{translate("First")}</label>
                      </div>
                    </GridItem>
                    {/* Gap */}
                    <GridItem width={twoColumnsBreakpoints}>
                      <div style={{ width: "100%" }}>
                                              <TextField onChange={lastNameChangeHandler} label=" " error={showValidations && !lastName ? error : ""}
                          value={lastName}
                        /><label>{translate("Last")}</label>
                      </div>
                    </GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem width={8} {...styles.formGridItem}>
                  <DatePicker disabled
                    selectedDay={todayDate}
                    onDayChange={todayDateChangeHandler} label={translate("Today's Date")} cssOverrides={{
                      formInputWrapper: css`
                                            .react-datetime-picker
                                            {
                                              color: #000!important;
                                            }
                                        `,
                    }}
                    required error={showValidations && !todayDate ? error : ""}
                  />
                </GridItem>
                <GridItem width={8} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}>
                      <Select label={translate("Specialty")} placeholder={translate("Specialty")} value={specialty} onChange={specialtyChangeHandler} required error={showValidations && !specialty ? error : ""}>
                        {specialtyOptions.map((option, index) => {
                          return <option key={index} value={option.key}>{option.value}</option>;
                        })}
                      </Select>
                    </GridItem>
                    {/* Gap */}
                    <GridItem width={twoColumnsBreakpoints}>
                      <Select label={translate("Area of Interest")} placeholder={translate("Area of Interest")} value={areaOfInterest} onChange={areaOfInterestChangeHandler} required error={showValidations && !areaOfInterest ? error : ""}>
                        {areaOfInterestOptions.map((option, index) => {
                          return <option key={index} value={option.key}>{option.value}</option>;
                        })}
                      </Select>
                    </GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem width={8} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}><TextField onChange={practiceHospitalChangeHandler}
                      label={translate("Practice/Hospital Name")} value={practiceHospital} required error={showValidations && !practiceHospital ? error : ""}
                    /></GridItem>
                    {/* Gap */}
                    <GridItem width={twoColumnsBreakpoints}>
                      <Select label={translate("Please Choose Your Country")} placeholder={translate("Please Choose Your Country")} value={country} onChange={countryChangeHandler} required error={showValidations && !country ? error : ""}>
                        {countries.map((option, index) => {
                          return <option key={index} value={option.key}>{option.value}</option>;
                        })}
                      </Select>
                    </GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem width={breakpoints} {...styles.formGridItem}><strong>{translate("Address*")}</strong></GridItem>
                <GridItem width={breakpoints} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}>
                      <div style={{ width: "100%" }}> <TextField value={streetAddress} required error={showValidations && !streetAddress ? error : ""}
                        onChange={streetAddressChangeHandler} label=""
                      />
                        <label>{translate("Street Address")}</label>
                      </div>
                    </GridItem>
                  </GridContainer>
                </GridItem>

                <GridItem width={breakpoints} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}>
                      <div style={{ width: "100%" }}> <TextField value={streetAddress2}
                        onChange={streetAddress2ChangeHandler} label=""
                      />
                        <label>{translate("Address Line 2")}</label>
                      </div>
                    </GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem width={breakpoints} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}>
                      <div style={{ width: "100%" }}><TextField onChange={cityChangeHandler} required error={showValidations && !city ? error : ""}
                        label="" value={city}
                      />
                        <label>{translate("City")}</label>
                      </div></GridItem>
                    {/* Gap */}
                    {country === "United States" && <GridItem width={twoColumnsBreakpoints}>
                      <div style={{ width: "100%" }}>
                        <Select label="" placeholder="State" value={state} onChange={stateChangeHandler} required error={showValidations && !state ? error : ""}>
                          {usStates.map((option, index) => {
                            return <option key={index} value={option.key}>{option.value}</option>;
                          })}
                        </Select>
                        <label>{translate("State")}</label>
                      </div>
                    </GridItem>}

                    {country === "Canada" && <GridItem width={twoColumnsBreakpoints}>
                      <div style={{ width: "100%" }}>
                        <Select label="" placeholder="Province" value={state} onChange={stateChangeHandler} required error={showValidations && !state ? error : ""}>
                          {canadaStates.map((option, index) => {
                            return <option key={index} value={option.key}>{option.value}</option>;
                          })}
                        </Select>
                        <label>{translate("Province")}</label>
                      </div>
                    </GridItem>}
                  </GridContainer>
                </GridItem>
                <br />
                <GridItem width={breakpoints} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}>
                      <div style={{ width: "100%" }}><TextField onChange={zipChangeHandler} required error={showValidations && !zip ? error : ""}
                        label="" value={zip}
                      />
                        <label>{translate("ZIP/Postal Code")}</label>
                      </div></GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem width={breakpoints} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}>
                      <TextField onChange={phoneChangeHandler}
                        label={translate("Phone")} value={phone} required error={showValidations && !phone ? error : ""}
                      />
                    </GridItem>
                    {/* Gap */}
                    <GridItem width={twoColumnsBreakpoints}>
                      <TextField onChange={emailChangeHandler}
                        label={translate("Email")} value={email} required error={showValidations && !email ? error : ""}
                      />
                    </GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem width={12}><hr />
                </GridItem>
                <GridItem width={breakpoints} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}>
                      <div style={{ width: "100%" }}>
                        <TextField onChange={serialNumberChangeHandler}
                          label={translate("Serial Number(s) on Unit")} value={serialNumbers} required error={showValidations && !serialNumbers ? error : ""}
                        />
                        <Notes>{fields.serialNumbersNote}</Notes>
                      </div>
                    </GridItem>
                    {/* Gap */}
                    <GridItem width={twoColumnsBreakpoints}>
                      <div style={{ width: "100%" }}>
                        <TextField onChange={modelNumberChangeHandler}
                          label={translate("Model Number(s)")} value={modelNumbers} required error={showValidations && !modelNumbers ? error : ""}
                        />
                        <Notes>{fields.modelNumbersNote}</Notes>
                      </div>
                    </GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem width={breakpoints} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}>
                      <div style={{ width: "100%" }}>
                        <TextField onChange={invoiceNumberChangeHandler}
                          label={translate("Invoice Number(s)")} value={invoiceNumbers} required error={showValidations && !invoiceNumbers ? error : ""}
                        />
                        <Notes>{fields.invoiceNumberNote}</Notes>
                      </div>
                    </GridItem>
                    {/* Gap */}
                    <GridItem width={twoColumnsBreakpoints}>
                      <div style={{ width: "100%" }}>
                        <label style={{marginBottom: "10px", fontWeight:700,display: "block"}}>{translate("Attach a Copy of Your Invoice(s)")}</label>
                        <FileUpload required onFileChange={fileChangeHandler} multiple={true} accept=".jpg,.jpeg,.pdf"
                          labelPosition="top" name="files"
                          error={incorrectFileExtension ? translate("This type of file is not allowed. Must be one of the following: jpg,jpeg,pdf") : undefined}
                          label="" {...styles.fileUploadBox}  disabled={maxFileCountReached} />
                        {files.map((value, index) => {
                          // <ButtonIcon src={Trash} onClick={deleteFile(value)} />
                          // <Typography onClick={deleteFile(value)}><Trash/></Typography>
                          return <><Notes key={index}>{value.name} {value.size / 1024 > 1024 ? `${floor((value.size / 1024) / 1024)}MB` : `${floor(value.size / 1024)}KB`}<span onClick={() => deleteFile(value)}><Trash /></span></Notes></>
                        })}
                        {files.map((file, index) => {
                          return (file.size > 10485760 && <Notes key={index}>{file.name} - {fields.fileSizeLimitMessage}</Notes>)
                        })}
                        {maxFileCountReached && <CountExceedNotes>{fields.maxFileCountLimitMessage}</CountExceedNotes>}
                        {maxSizeReached && <Notes>- {fields.fileSizeLimitMessage}</Notes>}
                        {incorrectFileExtension && <Notes>{fields.incorrectFileTypeMessage}</Notes>}
                        <Notes>{fields.invoiceAttachmentAcceptedFileTypesNote}</Notes>
                        <Notes>{fields.invoiceAttachmentNote}</Notes>
                      </div>
                    </GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem width={breakpoints} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}>
                      <TextField onChange={physicianLicenseChangeHandler}
                        label={translate("Physician State of Licensure and License Number")} value={physicianLicense} required error={showValidations && !physicianLicense ? error : ""}
                      /></GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem width={breakpoints} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}>
                      <TextField onChange={salesRepOrDistributorChangeHandler}
                        label={translate("My Sales Rep/ Distributor is")} value={salesRepOrDistributor} required error={showValidations && !salesRepOrDistributor ? error : ""}
                      /></GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem width={breakpoints} {...styles.formGridItem}>
                  <GridContainer>
                    <GridItem width={twoColumnsBreakpoints}>
                      <TextArea onChange={additionalNotesChangeHandler}
                        label={translate("Any Additional Notes?")} value={additionalNotes} rows={10}
                      /></GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
              <Zone contentId={id} zoneName="Content" />
              <ReCaptcha  recaptchaRef={recaptchaRef} location="ContactUs" />
              <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                {submitButtonText}
              </Button>
              <Modal
                headline={translate("Message Sent")}
                contentLabel={successMessage}
                {...styles.successModal}
                isOpen={isModalOpen}
                handleClose={modalCloseHandler}
              >
                <GridContainer {...styles.modalContainer}>
                  <GridItem {...styles.messageTextGridItem}>
                    <Typography {...styles.messageText}>{successMessage}</Typography>
                  </GridItem>
                  <GridItem {...styles.buttonGridItem}>
                    <Button {...styles.continueButton} onClick={continueButtonClickHandler}>
                      {translate("Continue")}
                    </Button>
                  </GridItem>
                </GridContainer>
              </Modal>
            </form>
          </RedemptionFormContext.Provider>
        </GridItem>
      </GridContainer>
    </>
  );
};

const widgetModule: WidgetModule = {
  component: connect(mapStateToProps, mapDispatchToProps)(RedemptionForm),
  definition: {
    group: "Contact Us",
    icon: "input-pipe",
    fieldDefinitions: [

      {
        name: fields.title,
        displayName: "Title of the form",
        editorTemplate: "TextField",
        fieldType: "Translatable",
        defaultValue: "Redemption Form",
      },
      {
        name: fields.instructions,
        displayName: "Instructions",
        editorTemplate: "RichTextField",
        fieldType: "Translatable",
        defaultValue: "",
      },
      {
        name: fields.serialNumbersNote,
        displayName: "‘Serial Number(s) On Unit’ Note(s)",
        editorTemplate: "TextField",
        fieldType: "Translatable",
        defaultValue: "note: to enter multiple units, please separate each number with a comma",
      },
      {
        name: fields.modelNumbersNote,
        displayName: "‘Model Number(s)’ Note(s)",
        editorTemplate: "TextField",
        fieldType: "Translatable",
        defaultValue: "note: to enter multiple units, please separate each number with a comma",
      },
      {
        name: fields.invoiceNumberNote,
        displayName: "‘Invoice Number(s)’ Note(s)",
        editorTemplate: "TextField",
        fieldType: "Translatable",
        defaultValue: "note: to enter multiple units, please separate each number with a comma.",
      },
      {
        name: fields.invoiceAttachmentNote,
        displayName: "‘Invoice Attachment’ Note(s)",
        editorTemplate: "TextField",
        fieldType: "Translatable",
        defaultValue: "note: jpg or pdf Image Format Required. Multiple images may be uploaded",
      }, {
        name: fields.invoiceAttachmentAcceptedFileTypesNote,
        displayName: "‘Invoice Attachment’ Accepted File Types Note(s)",
        editorTemplate: "TextField",
        fieldType: "Translatable",
        defaultValue: "Accepted file types: jpg, jpeg, pdf, Max. file size: 10 MB, Max. files: 5.",
      }, {
        name: fields.incorrectFileTypeMessage,
        displayName: "‘Incorrect File Type Error Message",
        editorTemplate: "TextField",
        fieldType: "Translatable",
        defaultValue: "This type of file is not allowed. Must be one of the following: jpg,jpeg,pdf",
      }, {
        name: fields.fileSizeLimitMessage,
        displayName: "‘Incorrect File Type Error Message",
        editorTemplate: "TextField",
        fieldType: "Translatable",
        defaultValue: "File exceeds size limit",
      }, {
        name: fields.maxFileCountLimitMessage,
        displayName: "‘Incorrect File Type Error Message",
        editorTemplate: "TextField",
        fieldType: "Translatable",
        defaultValue: "Maximum number of files reached",
      },
      {
        name: fields.emailRecipients,
        displayName: "Email recipients",
        editorTemplate: "MultilineTextField",
        fieldType: "General",
        defaultValue: "testaccount@insitesoft.com",
        isRequired: true,
        regularExpression: emailsRegexp,
      },
      {
        name: fields.submitButtonText,
        displayName: "Submit Button Text",
        editorTemplate: "TextField",
        fieldType: "Translatable",
        defaultValue: "Send",
      },
      {
        name: fields.successMessage,
        displayName: "Submit Success Message",
        editorTemplate: "RichTextField",
        fieldType: "Translatable",
        defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
      },
    ],
  },
};

export default widgetModule;

